import { BaseInput, ErrorMessage, Text } from '@shared/widgets';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Select } from '@shared/widgets/select';
import { IFormDataBusinessCreate } from '../../../payments/subPages/recipientEditor/subPages/businessRecipient';
import { IFormDataBusinessBetween, IFormDataIndividualBetween } from '../betweenAccount';
import { IFormDataBusinessOther, IFormDataIndividualOther } from '../otherTransfer';
import { IFormDataIndividualCreate } from '../../../payments/subPages/recipientEditor/subPages/individualRecipient';
import { $countryProvincesStore, $countryStore, $recipientMandatoryFieldsStore } from '@lib/store';
import { useUnit } from 'effector-react';
import { useMemo } from 'react';
import { ValidateUtils } from '@shared/utils';

export type AddressFormDataType =
  | IFormDataBusinessCreate
  | IFormDataBusinessBetween
  | IFormDataBusinessOther
  | IFormDataIndividualBetween
  | IFormDataIndividualCreate
  | IFormDataIndividualOther;

interface ICountryOptions {
  label: string;
  value: string;
}

interface PropsType {
  formControl: UseFormReturn<AddressFormDataType, undefined>;
  countryOptions: ICountryOptions[];
}

export const AddressFormBlock = (props: PropsType) => {
  const countryStore = useUnit($countryStore.store);
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);
  const countryProvincesStore = useUnit($countryProvincesStore.store);

  const companyCountry = props.formControl.watch('companyCountry');

  const selectedAddressProvinces = companyCountry in countryProvincesStore.data ? countryProvincesStore.data[companyCountry] : [];

  const countryOptions = useMemo(() => {
    return countryStore.data.map((x) => {
      return { label: x.name, value: x.twoLetterCode };
    });
  }, [countryStore]);

  return (
    <>
      <Text className='mt-6'>Address</Text>
      <Controller
        name='address'
        control={props.formControl.control}
        rules={{
          ...(mandatoryFields.data.includes('insi_address')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          maxLength: 100,
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Address');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='Address'
              onChange={onChange}
              required={mandatoryFields.data.includes('insi_address') ? true : false}
              value={value}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        name='postCode'
        control={props.formControl.control}
        rules={{
          ...(mandatoryFields.data.includes('insi_addresspostcode')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          maxLength: 10,
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'ZIP / Post Code');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='ZIP / Post Code'
              onChange={onChange}
              required={mandatoryFields.data.includes('insi_addresspostcode') ? true : false}
              value={value}
              maxLength={10}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        name='city'
        control={props.formControl.control}
        rules={{
          ...(mandatoryFields.data.includes('insi_addresscity')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          maxLength: 100,
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Town / City');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='Town / City'
              onChange={onChange}
              required={mandatoryFields.data.includes('insi_addresscity') ? true : false}
              value={value}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        control={props.formControl.control}
        name='province'
        rules={{
          maxLength: 100,
          ...(mandatoryFields.data.includes('insi_addressprovince')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <Select
              className='mt-2'
              wrapperClassName='overflow-hidden mb-2'
              buttonClassName='bg-transparent '
              iconClassName='!text-primary dark:!text-white'
              items={selectedAddressProvinces}
              nullValue={{ isEnable: true, text: 'Province / Region' }}
              required={mandatoryFields.data.includes('insi_addressprovince') ? true : false}
              value={value || null}
              enableSearch
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        control={props.formControl.control}
        name='companyCountry'
        rules={{ required: ValidateUtils.requiredErrorMessage }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <Select
              wrapperClassName='overflow-hidden'
              buttonClassName='bg-transparent '
              iconClassName='!text-primary dark:!text-white'
              items={countryOptions}
              nullValue={{ isEnable: true, text: 'Country' }}
              value={value}
              required
              enableSearch
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
    </>
  );
};
